// Show/hide bookkeeping for the "by type" (Beta) facility pin legend.
//
// The legend has one row per distinct pin appearance on the map, which is a pair of
// dimensions: facility type (operating / development) × highlighted-or-not. Each row maps
// 1:1 to a BY_TYPE_LAYER_IDS virtual layer, and the plain row means "the ones the active
// highlight metric does NOT match" — so hiding "Operating facility" while highlighting by
// "Listed on TractIQ" hides only the operating facilities that are not listed.
//
// A type is "split" (shows both rows) only when the active metric can actually highlight
// it; see BY_TYPE_HIGHLIGHTABLE_TYPES. With no metric selected nothing is split, which is
// the legend's default two-row state.
import { BY_TYPE_HIGHLIGHTABLE_TYPES, BY_TYPE_LAYER_IDS } from '../enums/FacilityPinConfig';

export const BY_TYPE_TYPES = Object.keys(BY_TYPE_LAYER_IDS);

export const DEFAULT_BY_TYPE_VISIBILITY = BY_TYPE_TYPES.reduce((acc, type) => {
    acc[BY_TYPE_LAYER_IDS[type].plain] = true;
    acc[BY_TYPE_LAYER_IDS[type].highlighted] = true;
    return acc;
}, {});

/**
 * Whether `type` shows a highlighted row under `metric` (i.e. the metric can match
 * features of that type at all).
 * @param {string|null} metric one of BY_TYPE_HIGHLIGHT_METRICS, or null for no highlight
 * @param {string} type 'operating' | 'development'
 */
export function isTypeSplit(metric, type) {
    const types = BY_TYPE_HIGHLIGHTABLE_TYPES[metric];
    return Boolean(types) && types.indexOf(type) !== -1;
}

/**
 * Carries hidden rows across a highlight-metric change, per type:
 *
 *   single → split   propagate: both halves inherit the single row's state, so hiding
 *                    "Development site" and then highlighting leaves both development
 *                    rows hidden (either can be brought back on its own).
 *   split → single   collapse: hidden only if BOTH halves were hidden — clearing a
 *                    highlight must never make a visible pin disappear.
 *   split → split    keep both, even though the populations change (the rows stay
 *                    visibly dimmed, so it's one click to recover).
 *   single → single  keep.
 *
 * Also covers development crossing in/out of `has-pricing`, where it has no highlighted
 * row: that's a per-type split/collapse like any other.
 *
 * @param {Object} visibility map of virtual-layer id → boolean
 * @param {string|null} prevMetric metric the map was showing
 * @param {string|null} nextMetric metric being selected
 * @returns {Object} a new visibility map
 */
export function reconcileByTypeVisibility(visibility, prevMetric, nextMetric) {
    const next = { ...visibility };

    BY_TYPE_TYPES.forEach(type => {
        const { plain, highlighted } = BY_TYPE_LAYER_IDS[type];
        const wasSplit = isTypeSplit(prevMetric, type);
        const isSplit = isTypeSplit(nextMetric, type);

        if (!wasSplit && isSplit) {
            next[highlighted] = visibility[plain] !== false;
        } else if (wasSplit && !isSplit) {
            const anyVisible = visibility[plain] !== false || visibility[highlighted] !== false;
            next[plain] = anyVisible;
            // Keep the unrendered key mirrored so a later split propagates the state the
            // user can actually see.
            next[highlighted] = anyVisible;
        }
    });

    return next;
}

/**
 * The layerVisibility payload for UPDATE_GEO_JSON_LAYER_VISIBILITY /
 * MAP_APPLY_FACILITY_PIN_CONFIG, filtering on BY_TYPE_LAYER_PROPERTY.
 *
 * All four entries are always sent. For a type with no highlighted row under the active
 * metric, its highlighted entry mirrors the plain one, so a feature that somehow lands in
 * that bucket can never be hidden by a row the user can't see or click.
 * @param {Object} visibility map of virtual-layer id → boolean
 * @param {string|null} metric active highlight metric
 */
export function byTypeLayerVisibility(visibility, metric) {
    const payload = [];

    BY_TYPE_TYPES.forEach(type => {
        const { plain, highlighted } = BY_TYPE_LAYER_IDS[type];
        const plainVisible = visibility[plain] !== false;
        payload.push({ layerId: plain, visible: plainVisible });
        payload.push({
            layerId: highlighted,
            visible: isTypeSplit(metric, type) ? visibility[highlighted] !== false : plainVisible,
        });
    });

    return payload;
}
